import type { Runner } from "@/lib/api";
import {
	RUNNER_STATE_LABELS,
	RUNNER_STATE_THEMES,
	RUNNER_UPDATE_REQUIRED_REASON,
	canUpdateRunner,
	runnerDisplayState,
} from "@/lib/runner";

const RUNNER_UPDATE_REQUIRED_MESSAGE = `${RUNNER_UPDATE_REQUIRED_REASON}「更新」を押して更新してください。`;

/**
 * 一覧に出す、デバイスの状態のバッジとランナーが報告した理由・更新の案内・自己診断の警告。
 * 警告はオフラインでも出す (再起動で起動しない・ログアウトで止まるなどは、オフラインになった理由そのもののため)。
 * 更新の案内は、横に「更新」が出ているときだけ出す。
 */
export function RunnerStateBadge({ runner }: { runner: Runner }) {
	const state = runnerDisplayState(runner);
	const theme = RUNNER_STATE_THEMES[state];
	// オフライン・解除済みのデバイスの理由は、最後のハートビートの古いもの。
	const message = runner.status === "active" && runner.online ? runner.runnerStatusMessage : null;
	return (
		<>
			<span
				data-testid={`runner-state-${runner.id}`}
				data-state={state}
				style={{
					display: "inline-block",
					padding: "2px 8px",
					borderRadius: "var(--radius-full)",
					background: theme.background,
					color: theme.color,
					fontSize: "var(--text-caption)",
					fontWeight: 500,
					whiteSpace: "nowrap",
				}}
			>
				{RUNNER_STATE_LABELS[state]}
			</span>
			{message && (
				<div
					data-testid={`runner-state-message-${runner.id}`}
					style={{ color: "var(--fg-3)", fontSize: "var(--text-caption)", marginTop: 4 }}
				>
					{message}
				</div>
			)}
			{runner.updateRequired && canUpdateRunner(runner) && (
				<div
					data-testid={`runner-update-required-${runner.id}`}
					style={{
						color: "var(--warning)",
						fontSize: "var(--text-caption)",
						lineHeight: "var(--leading-snug)",
						marginTop: 4,
					}}
				>
					{RUNNER_UPDATE_REQUIRED_MESSAGE}
				</div>
			)}
			{runner.status === "active" && runner.diagnostics.length > 0 && (
				<ul
					data-testid={`runner-diagnostics-${runner.id}`}
					style={{
						margin: "4px 0 0",
						padding: 0,
						listStyle: "none",
						color: "var(--warning)",
						fontSize: "var(--text-caption)",
						lineHeight: "var(--leading-snug)",
					}}
				>
					{runner.diagnostics.map((diagnostic, index) => (
						<li
							key={`${index}-${diagnostic.code}`}
							data-code={diagnostic.code}
							style={{ marginTop: 2 }}
						>
							{diagnostic.message}
						</li>
					))}
				</ul>
			)}
		</>
	);
}
